import React, { useState } from 'react';
import {
  Box, Alert, CircularProgress, Typography,
} from '@mui/material';
import { Tabs } from '../ui/Tabs';
import TrainingInsightsSummary from './TrainingInsightsSummary';
import TrainingInsightsDetails from './TrainingInsightsDetails';
import type { TrainingInsights } from './types';
import { ProgressBar } from '../ui/ProgressBar';
import { InsightSectionId, dimensionTotalFor } from './insightSections';

// Re-export so existing consumers (TrainingSessionEvaluation.tsx) keep working
// with `import TrainingInsightsTab, { TrainingInsights } from '...TrainingInsightsTab'`.
export type { TrainingInsights } from './types';

interface Props {
  insights: TrainingInsights | null | undefined;
  /** Optional "Re-run Analysis" control, rendered on the sub-tabs row
   *  (and beside the status alert when there are no insights yet). */
  reanalyzeButton?: React.ReactNode;
  /** Jump the session's conversation timeline to an ISO timestamp. */
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

/**
 * Training Insights tab — top-level container with Summary / Details
 * sub-tabs mirroring the post-call Call Insights pattern.
 */
const TrainingInsightsTab: React.FC<Props> = ({
  insights, reanalyzeButton, onNavigateToTimestamp,
}) => {
  const [tab, setTab] = useState<'summary' | 'details'>('summary');
  const [focusSection, setFocusSection] = useState<InsightSectionId | undefined>(undefined);

  const handleViewDetails = (sectionId: InsightSectionId) => {
    setFocusSection(sectionId);
    setTab('details');
  };

  if (!insights) {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Alert severity="info" sx={{ flex: 1 }}>
          Training insights are not available for this session.
        </Alert>
        {reanalyzeButton}
      </Box>
    );
  }

  if (insights.status === 'failed') {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Alert severity="error" sx={{ flex: 1 }}>
          Analysis failed{insights.error ? `: ${insights.error}` : '.'} Use
          Re-run Analysis to try again.
        </Alert>
        {reanalyzeButton}
      </Box>
    );
  }

  if (insights.status === 'not_scoreable') {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {insights.wasAbandoned && (
          <Alert severity="warning">
            <strong>Session Abandoned.</strong> The session ended before the AI customer finished.
          </Alert>
        )}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Alert severity="info" sx={{ flex: 1 }} data-testid="insights-not-scoreable">
            <strong>Not scored.</strong> The rep did not speak in this session, so there was
            nothing to grade.
          </Alert>
          {reanalyzeButton}
        </Box>
      </Box>
    );
  }

  const isPending = insights.status === 'pending';
  const dimsComplete = insights.dimensionsComplete?.length ?? 0;
  const dimsTotal = dimensionTotalFor(insights);

  return (
    <Box>
      {isPending && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              AI analysis is running: {dimsComplete} of {dimsTotal} dimensions ready.
              Refresh in a moment for the rest.
            </Typography>
          </Box>
          <ProgressBar value={(dimsComplete / dimsTotal) * 100} tone="info" />
        </Box>
      )}

      {/* Sub-tabs and the Re-run control share one row to save vertical space. */}
      <Tabs
        idPrefix="training-insights"
        ariaLabel="Training insights views"
        value={tab}
        onChange={(id) => {
          setFocusSection(undefined);
          setTab(id as typeof tab);
        }}
        items={[
          { id: 'summary', label: 'Summary' },
          { id: 'details', label: 'Details' },
        ]}
        actions={reanalyzeButton}
        className="mb-4"
      />

      {tab === 'summary' && (
        <TrainingInsightsSummary insights={insights} onViewDetails={handleViewDetails} />
      )}
      {tab === 'details' && (
        <TrainingInsightsDetails
          insights={insights}
          onNavigateToTimestamp={onNavigateToTimestamp}
          focusSection={focusSection}
        />
      )}
    </Box>
  );
};

export default TrainingInsightsTab;
